import React, { useState, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import html2pdf from "html2pdf.js";
import "./AttendanceTotalsModal.css";

const AttendanceTotalsModal = ({ isOpen, onClose, teamMembers, attendanceData }) => {
  const [viewMode, setViewMode] = useState("month");
  const [customStartDate, setCustomStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [offset, setOffset] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState("All");
  const [selectedUsers, setSelectedUsers] = useState([]);

  const pdfRef = useRef();

  const getDateRange = () => {
    let start, end;
    const base = new Date();

    if (viewMode === "week") {
      const day = base.getDay();
      const saturdayOffset = day === 6 ? 0 : -((day + 1) % 7);
      const weekStart = new Date(base);
      weekStart.setDate(base.getDate() + (offset * 7) + saturdayOffset);
      weekStart.setHours(0, 0, 0, 0);
      start = weekStart;
      end = new Date(weekStart);
      end.setDate(start.getDate() + 6);
    } else if (viewMode === "month") {
      const month = base.getMonth() + offset;
      start = new Date(base.getFullYear(), month, 1);
      end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    } else if (viewMode === "year" || viewMode === "yearByMonths") {
      const year = base.getFullYear() + offset;
      start = new Date(year, 0, 1);
      end = new Date(year, 11, 31);
    } else {
      start = customStartDate;
      end = customEndDate;
    }

    return { start, end };
  };

  const formatRangeLabel = ({ start, end }) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    if (viewMode === "week") {
      return `${start.toLocaleDateString(undefined, options)} - ${end.toLocaleDateString(undefined, options)}`;
    }
    if (viewMode === "month") {
      return start.toLocaleDateString(undefined, { year: "numeric", month: "long" });
    }
    if (viewMode === "year" || viewMode === "yearByMonths") {
      return start.getFullYear().toString();
    }
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const handleDownloadPDF = () => {
    html2pdf().from(pdfRef.current).save(`attendance-totals-${viewMode}.pdf`);
  };

  const { start, end } = getDateRange();

  const filteredMembers = teamMembers.filter((member) => {
    if (selectedTeam !== "All" && member.team !== selectedTeam) return false;
    if (selectedUsers.length > 0 && !selectedUsers.includes(member.id)) return false;
    return true;
  });

  const totalsByTeam = useMemo(() => {
    const dayCount = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const groups = {};

    filteredMembers.forEach((member) => {
      const team = member.team || "No Team";
      if (!groups[team]) groups[team] = [];

      const userRecords = attendanceData.filter((r) => {
        const recordDate = new Date(r.date);
        return r.userId === member.id && recordDate >= start && recordDate <= end;
      });

      const totals = {};
      userRecords.forEach((r) => {
        const code = r.key?.trim();
        if (code) {
          totals[code] = (totals[code] || 0) + 1;
        }
      });

      const percentages = {};
      Object.entries(totals).forEach(([code, count]) => {
        percentages[code] = ((count / dayCount) * 100).toFixed(1);
      });

      groups[team].push({
        name: `${member.first_name} ${member.last_name}`,
        totals,
        percentages,
      });
    });

    return groups;
  }, [attendanceData, filteredMembers, start, end]);

  if (!isOpen) return null;

  return (
    <div className="at-totals-modal-overlay">
      <div className="at-totals-modal">
        <div className="at-totals-modal-header">
          <h2>Attendance Totals</h2>
          <button onClick={onClose}>✖</button>
        </div>

        <div className="at-totals-controls">
          <button className="download-btn" onClick={handleDownloadPDF}>Download PDF</button>
          <select
            className="filter-dropdown"
            value={selectedTeam}
            onChange={(e) => {
              setSelectedTeam(e.target.value);
              setSelectedUsers([]);
            }}
          >
            <option value="All">All Teams</option>
            {Array.from(new Set(teamMembers.map((m) => m.team || "No Team"))).map((team) => (
              <option key={team} value={team}>{team}</option>
            ))}
          </select>
        </div>

        <div className="at-totals-mode-switch">
          <button onClick={() => { setOffset(0); setViewMode("week"); }} className={viewMode === "week" ? "active" : ""}>Week</button>
          <button onClick={() => { setOffset(0); setViewMode("month"); }} className={viewMode === "month" ? "active" : ""}>Month</button>
          <button onClick={() => { setOffset(0); setViewMode("year"); }} className={viewMode === "year" ? "active" : ""}>Year</button>
          <button onClick={() => { setOffset(0); setViewMode("yearByMonths"); }} className={viewMode === "yearByMonths" ? "active" : ""}>Year by Months</button>
          <button onClick={() => setViewMode("custom")} className={viewMode === "custom" ? "active" : ""}>Custom</button>
        </div>

        {viewMode === "custom" ? (
          <div className="at-totals-date-pickers">
            <span>From:</span>
            <DatePicker selected={customStartDate} onChange={setCustomStartDate} />
            <span>To:</span>
            <DatePicker selected={customEndDate} onChange={setCustomEndDate} />
          </div>
        ) : (
          <div className="at-totals-navigation centered-nav">
            <button onClick={() => setOffset(offset - 1)}>←</button>
            <span>{formatRangeLabel({ start, end })}</span>
            <button onClick={() => setOffset(offset + 1)}>→</button>
            <button className="today-btn" onClick={() => setOffset(0)}>Today</button>
          </div>
        )}

        <div className="at-totals-content" ref={pdfRef}>
          {viewMode === "yearByMonths" ? (
            <div className="year-by-months-grid">
              {/* Month Labels */}
              <div className="year-by-months-header-wrapper">
                <div className="year-by-months-header month-row">
                  <div className="user-name header">Name</div>
                  {Array.from({ length: 12 }, (_, i) => (
                    <div key={`month-label-${i}`} className="month-label">
                      {new Date(0, i).toLocaleString(undefined, { month: "long" })}
                    </div>
                  ))}
                </div>

                <div className="year-by-months-header code-label-row">
                  <div className="user-name header"></div>
                  {Array.from({ length: 12 }, (_, i) => (
                    <React.Fragment key={`subheader-${i}`}>
                      {["P", "T", "C", "L", "I"].map(code => (
                        <div key={`${i}-${code}`} className="ybm-col">{code}</div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              {/* Group by Team */}
              {Object.entries(
                filteredMembers.reduce((acc, member) => {
                  const team = member.team || "No Team";
                  if (!acc[team]) acc[team] = [];
                  acc[team].push(member);
                  return acc;
                }, {})
              ).map(([team, members]) => (
                <React.Fragment key={team}>
                  <div className="team-title">{team}</div>
                  {members.map((member, idx) => (
                    <div key={idx} className="ybm-user-row">
                      <div className="user-name">{member.first_name} {member.last_name}</div>
                      {Array.from({ length: 12 }, (_, month) => {
                        const monthStart = new Date(start.getFullYear(), month, 1);
                        const monthEnd = new Date(start.getFullYear(), month + 1, 0);
                        const monthDays = monthEnd.getDate();

                        const records = attendanceData.filter(r => {
                          const d = new Date(r.date);
                          return r.userId === member.id &&
                            d.getFullYear() === monthStart.getFullYear() &&
                            d.getMonth() === month;
                        });

                        const monthTotals = {};
                        records.forEach(r => {
                          const k = r.key?.trim();
                          if (k) {
                            monthTotals[k] = (monthTotals[k] || 0) + 1;
                          }
                        });

                        return ["PTO", "Tardy", "Call In", "Leave Early", "Incentive Day"].map(label => {
                          const count = monthTotals[label] || 0;
                          const pct = ((count / monthDays) * 100).toFixed(0);
                          return (
                            <div key={`${member.id}-${month}-${label}`} className="ybm-col">
                              {count ? `${count}` : ""}
                            </div>
                          );
                        });
                      })}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          ) : (
            Object.entries(totalsByTeam).map(([team, members]) => (
              <div key={team} className="team-group">
                {selectedTeam === "All" && <div className="team-title">{team}</div>}
                <div className="team-user-header-row">
                  <div className="user-name header">Name</div>
                  <div className="user-col">P</div>
                  <div className="user-col">T</div>
                  <div className="user-col">C</div>
                  <div className="user-col">L</div>
                  <div className="user-col">I</div>
                </div>
                {members.map((m, idx) => (
                  <div key={idx} className="team-user-row">
                    <div className="user-name">{m.name}</div>
                    {["PTO", "Tardy", "Call In", "Leave Early", "Incentive Day"].map(label => {
                      const count = m.totals[label] || 0;
                      const percentage = m.percentages[label] || 0;
                      return <div key={label} className="user-col">{count ? `${count} (${percentage}%)` : ""}</div>;
                    })}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceTotalsModal;
